import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ActionDetailModal from '../common/ActionDetailModal';

const MitreHeatmap = ({ actions, title, webFindings = [] }) => {
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);

  // Define MITRE ATT&CK tactics in order
  const tactics = [
    'Reconnaissance',
    'Resource Development',
    'Initial Access',
    'Execution',
    'Persistence',
    'Privilege Escalation',
    'Defense Evasion',
    'Credential Access',
    'Discovery',
    'Lateral Movement',
    'Collection',
    'Command and Control',
    'Exfiltration',
    'Impact'
  ];

  // Process actions to create heatmap data
  const heatmapData = useMemo(() => {
    const data = {};

    // Initialize tactics
    tactics.forEach(tactic => {
      data[tactic] = {
        techniques: {},
        total: 0
      };
    });

    // Count techniques per tactic
    actions.forEach(action => {
      if (action.mitreAttackTechnique) {
        const { id, name, tactic } = action.mitreAttackTechnique;

        if (data[tactic]) {
          if (!data[tactic].techniques[id]) {
            data[tactic].techniques[id] = {
              name,
              count: 0,
              results: { successful: 0, detected: 0, blocked: 0, failed: 0, partial: 0, unknown: 0 },
              actions: []
            };
          }

          data[tactic].techniques[id].count++;
          data[tactic].total++;
          data[tactic].techniques[id].actions.push(action);

          if (action.result) {
            data[tactic].techniques[id].results[action.result]++;
          }
        }
      }
    });

    // Merge web findings into technique data when present
    if (webFindings && webFindings.length > 0) {
      webFindings.forEach(wf => {
        const tactic = wf.tactic;
        const techId = wf.techniqueId;
        if (tactic && techId && data[tactic]) {
          if (!data[tactic].techniques[techId]) {
            data[tactic].techniques[techId] = {
              name: wf.techniqueName || techId,
              count: 0,
              results: { successful: 0, detected: 0, blocked: 0, failed: 0, partial: 0, unknown: 0 },
              actions: [],
              webFindings: []
            };
          }
          if (!data[tactic].techniques[techId].webFindings) {
            data[tactic].techniques[techId].webFindings = [];
          }
          data[tactic].techniques[techId].webFindings.push(wf);
        }
      });
    }

    return data;
  }, [actions, webFindings]);

  // Calculate max count for color scaling
  const maxCount = useMemo(() => {
    let max = 0;
    Object.values(heatmapData).forEach(tactic => {
      Object.values(tactic.techniques).forEach(technique => {
        if (technique.count > max) max = technique.count;
      });
    });
    return max;
  }, [heatmapData]);

  // Get color based on count and results
  const getColor = (technique) => {
    if (!technique) return darkMode ? 'bg-gray-700' : 'bg-gray-100';

    const intensity = maxCount > 0 ? (technique.count / maxCount) : 0;
    const successRate = technique.count > 0
      ? technique.results.successful / technique.count
      : 0;

    if (successRate >= 0.8) {
      if (intensity > 0.66) return 'bg-green-600';
      if (intensity > 0.33) return 'bg-green-500';
      return 'bg-green-400';
    } else if (technique.results.detected > technique.results.successful) {
      if (intensity > 0.66) return 'bg-yellow-600';
      if (intensity > 0.33) return 'bg-yellow-500';
      return 'bg-yellow-400';
    } else if (technique.results.blocked > 0) {
      if (intensity > 0.66) return 'bg-red-600';
      if (intensity > 0.33) return 'bg-red-500';
      return 'bg-red-400';
    } else {
      if (intensity > 0.66) return 'bg-blue-600';
      if (intensity > 0.33) return 'bg-blue-500';
      return 'bg-blue-400';
    }
  };

  const getResultBadge = (result) => {
    const colors = {
      successful: 'bg-green-100 text-green-800',
      detected: 'bg-yellow-100 text-yellow-800',
      blocked: 'bg-red-100 text-red-800',
      failed: 'bg-gray-100 text-gray-800',
      partial: 'bg-blue-100 text-blue-800'
    };
    return colors[result] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : ''}`}>
          {title || t('mitre.title')}
        </h3>
        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {t('mitre.heatmapDescription') || 'Darker colors indicate more activity. Green = successful, Yellow = detected, Red = blocked, Blue = mixed results.'}
        </p>
      </div>

      {/* Heatmap Grid */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="grid grid-cols-1 gap-4">
            {tactics.map(tactic => {
              const tacticData = heatmapData[tactic];
              const techniqueCount = Object.keys(tacticData.techniques).length;

              return (
                <div key={tactic} className={`border rounded-lg p-4 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className="mb-3">
                    <h4 className={`font-semibold ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>{tactic}</h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {techniqueCount} technique{techniqueCount !== 1 ? 's' : ''} •
                      {tacticData.total} action{tacticData.total !== 1 ? 's' : ''}
                    </p>
                  </div>

                  {techniqueCount > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                      {Object.entries(tacticData.techniques)
                        .sort((a, b) => b[1].count - a[1].count)
                        .map(([techniqueId, technique]) => (
                        <div
                          key={techniqueId}
                          className={`p-3 rounded-lg text-white ${getColor(technique)} relative group cursor-pointer transition-all hover:scale-105 hover:ring-2 hover:ring-purple-400`}
                          onClick={() => setSelectedTechnique({ id: techniqueId, ...technique, tactic })}
                        >
                          <div className="font-bold text-sm flex items-center gap-1">
                            {techniqueId}
                            {technique.webFindings && technique.webFindings.length > 0 && (
                              <span className="inline-block w-2.5 h-2.5 rounded-full bg-cyan-400 border border-white" title={`${technique.webFindings.length} web finding(s)`}></span>
                            )}
                          </div>
                          <div className="text-xs opacity-90">{technique.count} use{technique.count !== 1 ? 's' : ''}</div>

                          {/* Hover tooltip */}
                          <div className="absolute bottom-full left-0 mb-2 w-64 bg-gray-900 text-white p-3 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            <div className="font-semibold mb-1">{technique.name}</div>
                            <div className="text-xs space-y-1">
                              <div>{t('common.all')}: {technique.count}</div>
                              {technique.results.successful > 0 && (
                                <div className="text-green-300">{t('actions.successful')}: {technique.results.successful}</div>
                              )}
                              {technique.results.detected > 0 && (
                                <div className="text-yellow-300">{t('actions.detected')}: {technique.results.detected}</div>
                              )}
                              {technique.results.blocked > 0 && (
                                <div className="text-red-300">{t('actions.blocked')}: {technique.results.blocked}</div>
                              )}
                              {technique.results.failed > 0 && (
                                <div className="text-gray-300">{t('actions.failed')}: {technique.results.failed}</div>
                              )}
                              {technique.results.partial > 0 && (
                                <div className="text-blue-300">{t('actions.partial')}: {technique.results.partial}</div>
                              )}
                              {technique.webFindings && technique.webFindings.length > 0 && (
                                <div className="text-cyan-300 mt-1 pt-1 border-t border-gray-600">
                                  {t('webTesting.webFindings') || 'Web Findings'}: {technique.webFindings.length}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-sm ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                      {t('mitre.noTechniques') || 'No techniques used in this tactic'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <h4 className={`font-semibold mb-2 ${darkMode ? 'text-white' : ''}`}>{t('mitre.legend') || 'Legend'}</h4>
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 text-sm ${darkMode ? 'text-gray-300' : ''}`}>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span>{t('mitre.mostlySuccessful') || 'Mostly Successful'}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span>{t('mitre.mostlyDetected') || 'Mostly Detected'}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span>{t('mitre.someBlocked') || 'Some Blocked'}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span>{t('mitre.mixedResults') || 'Mixed Results'}</span>
          </div>
          {webFindings && webFindings.length > 0 && (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-cyan-400 rounded-full"></div>
              <span>{t('webTesting.webFindings') || 'Web Findings'}</span>
            </div>
          )}
        </div>
        <div className={`mt-3 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
          {t('mitre.intensityNote') || 'Color intensity indicates frequency of use (darker = more frequent)'}
        </div>
      </div>

      {/* Technique Detail Panel */}
      {selectedTechnique && (
        <div className={`border rounded-lg p-6 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex justify-between items-start mb-4">
            <div>
              <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {selectedTechnique.id} - {selectedTechnique.name}
              </h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {selectedTechnique.tactic} • {selectedTechnique.count} action{selectedTechnique.count !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={() => setSelectedTechnique(null)}
              className={`p-2 rounded hover:bg-gray-200 ${darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500'}`}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Result summary */}
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(selectedTechnique.results).filter(([, v]) => v > 0).map(([result, count]) => (
              <span key={result} className={`px-3 py-1 rounded-full text-sm ${getResultBadge(result)}`}>
                {result}: {count}
              </span>
            ))}
          </div>

          {/* Action list */}
          <div className="space-y-2">
            {selectedTechnique.actions.map((action, idx) => (
              <div
                key={action._id || idx}
                className={`p-3 rounded-lg border cursor-pointer transition-all hover:ring-2 hover:ring-purple-400 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                onClick={() => setSelectedAction(action)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{action.title}</span>
                    {action.operator?.username && (
                      <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        by {action.operator.username}
                      </span>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${getResultBadge(action.result)}`}>
                    {action.result}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Web Findings section - only when present */}
          {selectedTechnique.webFindings && selectedTechnique.webFindings.length > 0 && (
            <div className="mt-4 pt-4 border-t border-cyan-200">
              <h5 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-cyan-400"></span>
                {t('webTesting.webFindings') || 'Web Findings'} ({selectedTechnique.webFindings.length})
              </h5>
              <div className="space-y-2">
                {selectedTechnique.webFindings.map((wf, idx) => {
                  const sevColors = {
                    critical: 'bg-red-100 text-red-800',
                    high: 'bg-orange-100 text-orange-800',
                    medium: 'bg-yellow-100 text-yellow-800',
                    low: 'bg-green-100 text-green-800',
                    informational: 'bg-blue-100 text-blue-800'
                  };
                  return (
                    <div key={wf._id || idx} className={`p-3 rounded-lg border-l-4 border-cyan-400 ${darkMode ? 'bg-gray-700' : 'bg-cyan-50'}`}>
                      <div className="flex items-center justify-between">
                        <span className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{wf.title}</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${sevColors[wf.severity] || 'bg-gray-100 text-gray-800'}`}>
                          {wf.severity}
                        </span>
                      </div>
                      {wf.wstgTestId && (
                        <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          WSTG: {wf.wstgTestId}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Detail Modal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        onActionUpdated={(updated) => setSelectedAction(updated)}
      />
    </div>
  );
};

export default MitreHeatmap;
